<script>
import { isNavigationFailure, NavigationFailureType } from 'vue-router'

import copyToClipBoard from '@utils/copy-to-clipboard'
import SocialShare from '@components/social-share'

export default {
    components: {
        SocialShare,
    },
    props: {
        channel: {
            type: Object,
            default: () => {},
        },
        // Defaults are the /radio channel-tile menu (channel-detail-tile.vue), which passes
        // neither: it keeps View Channel and gets no settings links. The channel-page hero
        // inverts both — the same way the artist hero passes `hasViewArtistOption: false`.
        hasViewChannelOption: {
            type: Boolean,
            default: true,
        },
        hasSettingsLinks: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        url() {
            const protocol = `${window.location.protocol}`
            const hostname = `${window.location.hostname}`
            const port = `${window.location.port}`
            return `${protocol}//${hostname}${port ? ':' + port : ''}${
                this.$router.resolve({ name: 'channel', params: { channelId: this.channel.id } }).href
            }`
        },
    },
    methods: {
        copyLinkToClipBoard() {
            copyToClipBoard(this.url)
            this.$store.dispatch('appearance/handleNotification', {
                message: this.$m('LINK_COPIED_CLIPBOARD'),
                type: 'success',
            })
        },
        goToAudioQuality() {
            return this._goToSettings('settings/audio-quality')
        },
        goToExternalPlayer() {
            return this._goToSettings('settings/external-player')
        },
        // Both settings routes are auth-gated (`beforeEnter: beforeEnterAuth(true)`), so a
        // signed-out member is redirected to login — and since vue-router 3.1 a push whose guard
        // redirects REJECTS its promise. That bounce is the intended behaviour, not a fault: the
        // global `beforeEach` has already stashed the destination, so login returns them here. So
        // swallow that one failure, and ONLY that one — every other rejection, navigation failure
        // or not, is a real fault and is rethrown as loudly as it would have been. The sidebar and
        // top-bar Settings links never needed this because a <router-link> swallows the failure
        // itself; these are the app's first programmatic pushes to an auth-gated route.
        _goToSettings(name) {
            return this.$router.push({ name }).catch(error => {
                if (!isNavigationFailure(error, NavigationFailureType.redirected)) throw error
            })
        },
    },
}
</script>

<template>
    <ul :class="$style.dropdownMenu">
        <li
            v-if="hasViewChannelOption"
            :class="$style.dropdownItem"
            @click="$router.push({ name: 'channel', params: { channelId: channel.id } })"
        >
            <BaseButton
                icon="eye"
                size="medium"
                button-type="transparent"
                @click="() => {}"
            >&nbsp; View Channel</BaseButton>
        </li>
        <!-- The settings cluster and the separator that closes it appear together or not
             at all — one condition over the group, so a fourth link cannot be added and
             left ungated. -->
        <template v-if="hasSettingsLinks">
            <li
                :class="$style.dropdownItem"
                @click="goToAudioQuality"
            >
                <BaseButton
                    icon="headphones"
                    size="medium"
                    button-type="transparent"
                    @click="() => {}"
                >&nbsp;Audio Quality</BaseButton>
            </li>
            <li
                :class="$style.dropdownItem"
                @click="goToExternalPlayer"
            >
                <BaseButton
                    icon="tv-speaker"
                    size="medium"
                    button-type="transparent"
                    @click="() => {}"
                >&nbsp;Play in External Player</BaseButton>
            </li>
            <div :class="$style.separator" />
        </template>
        <li :class="$style.dropdownItem">
            <BaseFavourites
                v-if="channel"
                :channel="channel"
                size="medium"
                button-type="transparent"
            >
                <span slot="main">Add {{ channel.title }} to Favorites</span>
                <span slot="alt">Remove {{ channel.title }} from Favorites</span>
            </BaseFavourites>
        </li>
        <h6 :class="$style.shareTitle">Share</h6>
        <div :class="$style.separator" />
        <li :class="$style.dropdownItem">
            <BaseButton
                icon="facebook"
                size="medium"
                button-type="transparent"
            >
                <SocialShare
                    :url="url"
                    type="facebook"
                />
            </BaseButton>
        </li>
        <li :class="$style.dropdownItem">
            <BaseButton
                icon="twitter"
                size="medium"
                button-type="transparent"
            >
                <SocialShare
                    :url="url"
                    :custom-text="`${channel.title} on @friskyradio #feelinfrisky`"
                    type="twitter"
                />
            </BaseButton>
        </li>
        <li
            :class="$style.dropdownItem"
            @click="copyLinkToClipBoard"
        >
            <BaseButton
                icon="link"
                size="medium"
                button-type="transparent"
                @click="() => {}"
            >&nbsp;Copy link</BaseButton>
        </li>
    </ul>
</template>

<style lang="scss" module>
@import '@design';

.dropdown {
    &Menu {
        padding: 0;
        margin: 0;
        text-align: left;
        list-style: none;
    }
    &Item {
        display: flex;
        align-items: center;
        height: 44px;
        padding: 0 $s-m;
    }
}

.separator {
    position: relative;
    display: block;
    width: 100%;
    height: 1px;
    background: $c-mine-shaft;
}

.shareTitle {
    @extend %contentTitleM;

    margin: $s-s 0 $s-s $s-xl;
}
</style>
